import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from 'react-query';
import axios from 'axios';
import { 
  SparklesIcon,
  UserGroupIcon,
  ChartBarIcon,
  HeartIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const MatchingEngine = () => {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Récupérer les matchings pour l'utilisateur
  const { data: matchingData, refetch } = useQuery(
    'matching-suggestions',
    async () => {
      const response = await axios.get('/api/matching/suggestions');
      return response.data;
    },
    {
      enabled: !!user,
      refetchInterval: 30000 // Rafraîchir toutes les 30 secondes
    }
  );

  useEffect(() => {
    if (matchingData) {
      setMatches(matchingData);
      setLoading(false);
    }
  }, [matchingData]);

  const getMatchScore = (score) => {
    if (score >= 90) return { color: 'text-green-600', label: 'Excellent' };
    if (score >= 80) return { color: 'text-blue-600', label: 'Très bon' };
    if (score >= 70) return { color: 'text-yellow-600', label: 'Bon' };
    if (score >= 60) return { color: 'text-orange-600', label: 'Correct' };
    return { color: 'text-red-600', label: 'Faible' };
  };

  const handleContactMatch = async (match) => {
    try {
      // Créer une conversation avec le match
      await axios.post('/api/messages', {
        recipientId: match.userId,
        content: `Bonjour ! J'ai vu que nous avons un excellent matching (${match.score}%) sur votre projet "${match.projectTitle}". J'aimerais en discuter avec vous.`
      });
      
      // Marquer le match comme contacté
      await axios.post('/api/matching/contact', {
        matchId: match.id
      });
      
      refetch();
    } catch (error) {
      console.error('Erreur contact match:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <SparklesIcon className="h-6 w-6 text-primary-500 mr-2" />
          <h2 className="text-xl font-semibold text-text">
            Suggestions de matching
          </h2>
        </div>
        <p className="text-gray-600 mb-6">
          Notre algorithme intelligent analyse vos préférences et vous propose 
          les meilleures correspondances avec des entrepreneurs et investisseurs.
        </p>

        {matches.length === 0 ? (
          <div className="text-center py-8">
            <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucune suggestion de matching pour le moment</p>
            <p className="text-sm text-gray-500 mt-1">
              Complétez votre profil pour recevoir des suggestions personnalisées
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matches.map((match) => {
              const scoreInfo = getMatchScore(match.score);
              
              return (
                <div key={match.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      {match.userAvatar ? (
                        <img
                          className="h-10 w-10 rounded-full mr-3"
                          src={match.userAvatar}
                          alt={match.userName}
                        />
                      ) : (
                        <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-primary-600">
                            {match.userName[0]}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium text-text">{match.userName}</h3>
                        <p className="text-sm text-gray-600 capitalize">{match.userRole}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`text-lg font-bold ${scoreInfo.color}`}>
                        {match.score}%
                      </div>
                      <div className={`text-xs ${scoreInfo.color}`}>
                        {scoreInfo.label}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-medium text-text mb-1">{match.projectTitle}</h4>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {match.projectDescription}
                    </p>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Financement:</span>
                      <span className="font-medium">{match.fundingNeeded}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Pays:</span>
                      <span className="font-medium">{match.country}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Catégorie:</span>
                      <span className="font-medium">{match.category}</span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleContactMatch(match)}
                      className="flex-1 bg-primary-500 text-white px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4 inline mr-1" />
                      Contacter
                    </button>
                    <button className="p-2 text-gray-600 hover:text-red-500 transition-colors">
                      <HeartIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Statistiques de matching */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-blue-500 mr-3" />
            <div>
              <h3 className="font-semibold text-text">Score moyen</h3>
              <p className="text-2xl font-bold text-blue-600">
                {matches.length > 0 
                  ? Math.round(matches.reduce((sum, match) => sum + match.score, 0) / matches.length)
                  : 0}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-green-500 mr-3" />
            <div>
              <h3 className="font-semibold text-text">Matchs trouvés</h3>
              <p className="text-2xl font-bold text-green-600">{matches.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-purple-500 mr-3" />
            <div>
              <h3 className="font-semibold text-text">Contacts initiés</h3>
              <p className="text-2xl font-bold text-purple-600">
                {matches.filter(m => m.contacted).length}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingEngine;

